import React, { useEffect, useState } from "react";
import oauthService from "../services/oauth.service";

/**
 * Authentication Wrapper
 * Protects your app and shows 401 Unauthorized if not authenticated
 */
const AuthWrapper = ({ children }) => {
  const [authState, setAuthState] = useState({
    status: "checking", // checking | authenticated | unauthorized | error
    user: null,
    error: null,
  });

  useEffect(() => {
    checkAuthentication();
  }, []);

  const checkAuthentication = async () => {
    try {
      const result = await oauthService.authenticate();

      if (result.success) {
        setAuthState({
          status: "authenticated",
          user: result.user,
          error: null,
        });
      } else if (result.requiresLogin) {
        setAuthState({
          status: "unauthorized",
          user: null,
          error: result.error,
        });
      } else {
        setAuthState({
          status: "error",
          user: null,
          error: result.error || "Authentication failed",
        });
      }
    } catch (error) {
      console.error("Authentication check failed:", error);
      setAuthState({
        status: "error",
        user: null,
        error: error.message,
      });
    }
  };

  const handleLoginClick = () => {
    oauthService.redirectToLogin();
  };

  // Checking authentication
  if (authState.status === "checking") {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <div style={styles.spinner}></div>
          <h2 style={styles.heading}>🔐 Authenticating...</h2>
          <p style={styles.text}>Verifying your credentials</p>
        </div>
      </div>
    );
  }

  // 401 Unauthorized
  if (authState.status === "unauthorized") {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <div style={styles.errorIcon}>🚫</div>
          <h1 style={styles.errorCode}>401</h1>
          <h2 style={styles.errorHeading}>Unauthorized Access</h2>
          <p style={styles.text}>
            You must be logged in to access this application.
          </p>
          <p style={styles.subText}>
            Please sign in with your VplaySmart account to continue.
          </p>
          <button
            onClick={handleLoginClick}
            style={styles.loginButton}
            onMouseOver={(e) => (e.target.style.backgroundColor = "#45a049")}
            onMouseOut={(e) => (e.target.style.backgroundColor = "#4CAF50")}
          >
            Login with VplaySmart
          </button>
          {oauthService.isInIframe() && (
            <p style={styles.iframeNote}>
              <small>
                📱 This app is embedded in an iframe. Login will open in the
                parent window.
              </small>
            </p>
          )}
        </div>
      </div>
    );
  }

  // Error state
  if (authState.status === "error") {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <div style={styles.errorIcon}>⚠️</div>
          <h2 style={styles.errorHeading}>Authentication Error</h2>
          <p style={styles.text}>An error occurred during authentication.</p>
          <p style={styles.errorMessage}>{authState.error}</p>
          <button
            onClick={checkAuthentication}
            style={styles.retryButton}
            onMouseOver={(e) => (e.target.style.backgroundColor = "#2980b9")}
            onMouseOut={(e) => (e.target.style.backgroundColor = "#3498db")}
          >
            Retry Authentication
          </button>
        </div>
      </div>
    );
  }

  // Authenticated - render the app
  return children;
};

const styles = {
  container: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: "100vh",
    backgroundColor: "#f0f2f5",
    fontFamily:
      '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    padding: "20px",
  },
  card: {
    backgroundColor: "white",
    borderRadius: "12px",
    boxShadow: "0 4px 20px rgba(0, 0, 0, 0.1)",
    padding: "50px 40px",
    textAlign: "center",
    maxWidth: "500px",
    width: "100%",
  },
  spinner: {
    border: "4px solid #f3f3f3",
    borderTop: "4px solid #4CAF50",
    borderRadius: "50%",
    width: "50px",
    height: "50px",
    animation: "spin 1s linear infinite",
    margin: "0 auto 20px",
  },
  heading: {
    fontSize: "24px",
    fontWeight: "600",
    color: "#333",
    marginBottom: "12px",
    marginTop: 0,
  },
  errorIcon: {
    fontSize: "64px",
    marginBottom: "20px",
  },
  errorCode: {
    fontSize: "80px",
    fontWeight: "bold",
    color: "#e74c3c",
    margin: "10px 0",
    letterSpacing: "-2px",
  },
  errorHeading: {
    fontSize: "28px",
    fontWeight: "600",
    color: "#333",
    marginBottom: "16px",
    marginTop: 0,
  },
  text: {
    fontSize: "16px",
    color: "#666",
    marginBottom: "12px",
    lineHeight: "1.6",
  },
  subText: {
    fontSize: "14px",
    color: "#888",
    marginBottom: "30px",
    lineHeight: "1.5",
  },
  errorMessage: {
    fontSize: "13px",
    color: "#e74c3c",
    backgroundColor: "#fee",
    padding: "12px 16px",
    borderRadius: "6px",
    marginBottom: "20px",
    fontFamily: "monospace",
    wordBreak: "break-word",
  },
  loginButton: {
    backgroundColor: "#4CAF50",
    color: "white",
    border: "none",
    borderRadius: "8px",
    padding: "14px 40px",
    fontSize: "16px",
    fontWeight: "600",
    cursor: "pointer",
    transition: "all 0.3s ease",
    boxShadow: "0 2px 8px rgba(76, 175, 80, 0.3)",
    marginTop: "10px",
  },
  retryButton: {
    backgroundColor: "#3498db",
    color: "white",
    border: "none",
    borderRadius: "8px",
    padding: "14px 40px",
    fontSize: "16px",
    fontWeight: "600",
    cursor: "pointer",
    transition: "all 0.3s ease",
    boxShadow: "0 2px 8px rgba(52, 152, 219, 0.3)",
  },
  iframeNote: {
    marginTop: "24px",
    padding: "14px",
    backgroundColor: "#e8f4f8",
    borderRadius: "8px",
    color: "#2c3e50",
    fontSize: "13px",
    lineHeight: "1.5",
  },
};

// Add CSS animation
if (typeof document !== "undefined") {
  const styleSheet = document.createElement("style");
  styleSheet.textContent = `
    @keyframes spin {
      0% { transform: rotate(0deg); }
      100% { transform: rotate(360deg); }
    }
  `;
  document.head.appendChild(styleSheet);
}

export default AuthWrapper;
